import {
  type ColorRepresentation,
  DataTexture,
  DoubleSide,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
  type Side,
  type Texture,
  UnsignedByteType,
} from 'three';

/**
 * Flat, poster-like shading: a 3-step toon ramp. dot(N, L) is remapped to
 * 0..1 and looked up with nearest filtering, so each surface gets at most
 * three flat tones (shade, mid, lit).
 */
export const RAMP = [0.32, 0.72, 1.0];

let gradient: DataTexture | null = null;

export function toonGradient(): DataTexture {
  if (gradient) return gradient;
  const data = new Uint8Array(RAMP.map((v) => Math.round(v * 255)));
  gradient = new DataTexture(data, RAMP.length, 1, RedFormat, UnsignedByteType);
  gradient.minFilter = NearestFilter;
  gradient.magFilter = NearestFilter;
  gradient.generateMipmaps = false;
  gradient.needsUpdate = true;
  return gradient;
}

export interface ToonOptions {
  vertexColors?: boolean;
  map?: Texture | null;
  side?: Side;
  transparent?: boolean;
  opacity?: number;
  doubleSide?: boolean;
  alphaTest?: number;
}

export function toonMaterial(color: ColorRepresentation = 0xffffff, o: ToonOptions = {}): MeshToonMaterial {
  const m = new MeshToonMaterial({
    color,
    gradientMap: toonGradient(),
    vertexColors: o.vertexColors ?? false,
    map: o.map ?? null,
    transparent: o.transparent ?? false,
    opacity: o.opacity ?? 1,
    alphaTest: o.alphaTest ?? 0,
  });
  if (o.doubleSide) m.side = DoubleSide;
  else if (o.side !== undefined) m.side = o.side;
  return m;
}

let shared: MeshToonMaterial | null = null;

/** One material for every merged, vertex-coloured mesh in the world. */
export function vertexColorToon(): MeshToonMaterial {
  if (!shared) shared = toonMaterial(0xffffff, { vertexColors: true });
  return shared;
}
